/**
 * buildMessageTokenUsage.ts — single source of truth for mapping the SSE
 * `done` event's wire token-usage into the ChatMessage `tokenUsage` /
 * `tokenUsages` fields the ⓘ info popup renders.
 *
 * Why this exists (2026-07-13): the ⓘ info popup must show accurate token
 * usage on EVERY assistant message/card, on every account, page, and view —
 * a hard consistency requirement from the owner. Two `onDone` branches in
 * useGlobalTools built the message: the streaming-finalize branch (text
 * turns) attached usage; the ORPHAN research-deliverable branch (surface-
 * owning full-pipeline Deep Research reports, which emit no text chunks so
 * they render as a PDF/DOCX tile) intentionally deferred usage
 * (buildResearchDeliverableMessage's original header: "Token usage on the
 * orphan-attachment path is a follow-up if needed"). The result: a full
 * deep-research report — qualification + blueprint + multi-minute synthesis
 * + deliverables — showed "no extensive compute was necessary for this
 * message" in the popup.
 *
 * The fix is to route BOTH branches through this one mapper so the two paths
 * can never drift again. The estimation heuristics for missing input/output
 * counts are lifted verbatim from the streaming-finalize branch; the caller
 * supplies the output text length (accumulated stream text, or the report
 * body on the deliverable path) and the user-message length so the fallback
 * estimate is path-appropriate.
 *
 * Pure: no React, no clock injection beyond `new Date()` for the stamp, no
 * I/O. Testable in isolation.
 */

import type {
  TokenUsageMetadata,
  AIProvider,
} from '../../../components/GlobalChatSystem/types';

/** Loose wire shape of a usage entry as it arrives on the SSE `done` event. */
export interface WireTokenUsage {
  inputTokens?: number;
  outputTokens?: number;
  model?: string;
  provider?: string;
  upstreamCostUsd?: number;
  reasoningTokens?: number;
  /** Request-side reasoning-effort level (none…max) when the backend stamped it. */
  effort?: string;
  /** Step billed on its own lane — the ⓘ shows it labeled, totals include it. */
  billedSeparately?: boolean;
  /** Deterministic sidecar dedupe/label key (`memext_…` / `threadfup_…`). */
  sidecarKey?: string;
}

export interface BuildMessageTokenUsageOptions {
  /**
   * Length (chars) of the visible model output for this turn — accumulated
   * stream text on the streaming path, or the report markdown body on the
   * deliverable path. Only used to estimate output tokens when the wire
   * count is missing/unreliable.
   */
  outputTextLength?: number;
  /** Length (chars) of the user's message, for the input-token estimate fallback. */
  userMessageLength?: number;
}

function normalizeProvider(provider?: string): AIProvider | undefined {
  return (provider && provider !== 'unknown' ? provider : 'openrouter') as
    | AIProvider
    | undefined;
}

/**
 * Map a single wire usage entry into the ChatMessage `tokenUsage` shape,
 * applying the VM-path reliability heuristics (input < 500 or output <= 10
 * are treated as truncated gateway counts and estimated from text length).
 */
function mapPrimaryUsage(
  tokenUsage: WireTokenUsage,
  opts: BuildMessageTokenUsageOptions,
): TokenUsageMetadata {
  const outputTextLength = opts.outputTextLength ?? 0;
  const userMessageLength = opts.userMessageLength ?? 0;

  const rawOutput = tokenUsage.outputTokens ?? 0;
  const rawInput = tokenUsage.inputTokens ?? 0;

  // Output is real if > 10 (a genuine response is at least a few sentences).
  const hasRealOutput = rawOutput > 10;
  // Input is real only if > 500 (VM/OpenClaw gateway otherwise counts only the
  // last user message, not the full context window).
  const hasRealInput = rawInput > 500;

  const estimatedOutput = hasRealOutput
    ? rawOutput
    : Math.max(50, Math.round(outputTextLength / 4));

  const estimatedInput = hasRealInput
    ? rawInput
    : Math.max(3000, 3000 + Math.round(userMessageLength / 4));

  // Never fabricate a model id — leave undefined so the popup resolver falls
  // back to the provider's codename family.
  const model =
    tokenUsage.model && tokenUsage.model !== 'unknown' ? tokenUsage.model : undefined;

  // Only carry an authoritative upstream cost (|| so a backend-sent 0 is
  // treated as missing and priced by the client's per-model rates instead).
  const upstreamCostUsd = tokenUsage.upstreamCostUsd || undefined;

  return {
    inputTokens: estimatedInput,
    outputTokens: estimatedOutput,
    ...(tokenUsage.reasoningTokens !== undefined && tokenUsage.reasoningTokens > 0
      ? { reasoningTokens: tokenUsage.reasoningTokens }
      : {}),
    model,
    provider: normalizeProvider(tokenUsage.provider),
    ...(upstreamCostUsd !== undefined ? { upstreamCostUsd } : {}),
    ...(tokenUsage.effort ? { effort: tokenUsage.effort } : {}),
    timestamp: new Date(),
  };
}

/** Map a per-model usage entry (multi-model turns) — pass-through, no estimation. */
function mapBreakdownUsage(u: WireTokenUsage): TokenUsageMetadata {
  return {
    inputTokens: u.inputTokens,
    outputTokens: u.outputTokens,
    ...(u.reasoningTokens !== undefined && u.reasoningTokens > 0
      ? { reasoningTokens: u.reasoningTokens }
      : {}),
    model: u.model,
    provider: u.provider as AIProvider | undefined,
    ...(u.upstreamCostUsd ? { upstreamCostUsd: u.upstreamCostUsd } : {}),
    ...(u.effort ? { effort: u.effort } : {}),
    // 2026-08-16: sidecar steps (memory update / follow-up suggestions)
    // keep their lane flag + key through the live path too — dropping them
    // here left the ⓘ unable to label live-delivered background steps.
    ...(u.billedSeparately ? { billedSeparately: true } : {}),
    ...(u.sidecarKey ? { sidecarKey: u.sidecarKey } : {}),
    timestamp: new Date(),
  };
}

/**
 * Build the ChatMessage token-usage fields from the SSE `done` args.
 * Returns an object with `tokenUsage` and/or `tokenUsages` set only when the
 * corresponding wire data is present, so callers can spread it onto a message
 * without clobbering with empty fields.
 */
export function buildMessageTokenUsage(
  tokenUsage: WireTokenUsage | undefined | null,
  tokenUsages: ReadonlyArray<WireTokenUsage> | undefined | null,
  opts: BuildMessageTokenUsageOptions = {},
): { tokenUsage?: TokenUsageMetadata; tokenUsages?: TokenUsageMetadata[] } {
  const out: { tokenUsage?: TokenUsageMetadata; tokenUsages?: TokenUsageMetadata[] } = {};

  if (tokenUsage) {
    out.tokenUsage = mapPrimaryUsage(tokenUsage, opts);
  }

  if (tokenUsages && tokenUsages.length > 0) {
    out.tokenUsages = tokenUsages.map(mapBreakdownUsage);
  }

  return out;
}
